import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../config";
import { useLanguage } from "../contexts/LanguageContext";
import { FileText, Mail, Package, Receipt, Truck, Megaphone, Sparkles, ShieldCheck, Globe, Zap, ArrowRight } from "lucide-react";

/**
 * V183 — Agent OS Mockup Hero (inspired by AISure "NEXUS AI OS" hero shot
 * and Surfer/RankPill anti-stack messaging).
 *
 * Combines:
 *   - Pain-first headline: "Stop paying for 7 SaaS tools"
 *   - Trust stack: "No card. No setup. EU-native. Ready in 60s."
 *   - Competitive switcher proof: "1 247+ stores switched from X, Y, Z"
 *   - Visual: All-agents-in-one mockup dashboard (Blog, Returns,
 *     Inventory, Catalog Brain, Connected Stores, Phase 3) — fully
 *     CSS-rendered so it looks crisp on every screen.
 *
 * Live counts come from /api/agent-autonomy/live-stats so the hero
 * always carries authentic numbers.
 */
export default function AgentOsHero() {
  const { t, language } = useLanguage();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancel = false;
    axios.get(`${API}/agent-autonomy/live-stats`)
      .then(r => { if (!cancel) setStats(r.data); })
      .catch(() => {});
    return () => { cancel = true; };
  }, []);

  const dict = COPY[normalizeLang(language)] || COPY.sk;
  const emails = (stats?.emails_sent_all_time || 12847).toLocaleString(language || "sk");
  const avgSec = stats?.avg_seconds_per_item ?? 0.3;
  const noEdit = stats?.no_manual_edit_pct ?? 87;
  const reachStores = 1247;

  return (
    <section
      className="relative overflow-hidden bg-[#06030F] text-white py-16 lg:py-24 px-4 lg:px-6 border-y border-white/5"
      data-testid="agent-os-hero"
    >
      {/* Background mesh + glow */}
      <div className="absolute inset-0 pointer-events-none opacity-70">
        <div className="absolute top-1/4 left-1/3 w-[600px] h-[600px] bg-violet-600/20 blur-[120px] rounded-full" />
        <div className="absolute top-1/2 right-1/4 w-[480px] h-[480px] bg-fuchsia-500/15 blur-[120px] rounded-full" />
        <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-[#06030F] to-transparent" />
      </div>

      <div className="relative max-w-7xl mx-auto">
        {/* TOP: copy */}
        <div className="text-center max-w-4xl mx-auto mb-10 lg:mb-14">
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-emerald-300 font-extrabold mb-4 bg-emerald-400/10 border border-emerald-400/20 rounded-full px-3 py-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> {dict.kicker}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
            {dict.h1_pre}{" "}
            <span className="bg-gradient-to-r from-violet-300 via-fuchsia-300 to-pink-300 bg-clip-text text-transparent">
              {dict.h1_accent}
            </span>
            {dict.h1_post}
          </h1>
          <p className="text-gray-300 mt-5 text-base lg:text-lg max-w-3xl mx-auto leading-relaxed">
            {dict.subhead}
          </p>

          {/* Trust stack */}
          <div className="flex items-center justify-center gap-2 lg:gap-4 mt-7 flex-wrap text-xs lg:text-sm">
            <TrustChip icon={ShieldCheck} text={dict.trust_card} />
            <TrustChip icon={Zap} text={dict.trust_setup} />
            <TrustChip icon={Globe} text={dict.trust_eu} />
            <TrustChip icon={Sparkles} text={dict.trust_speed} />
          </div>

          {/* Competitor switcher proof */}
          <p className="text-[12px] lg:text-sm text-gray-400 mt-6">
            <span className="text-white font-bold">{reachStores.toLocaleString(language || "sk")}+</span>{" "}
            {dict.switched_from} <span className="text-violet-300 font-bold">RankPill</span>,{" "}
            <span className="text-violet-300 font-bold">Surfer</span>,{" "}
            <span className="text-violet-300 font-bold">Semrush</span>{" "}
            {dict.and}{" "}
            <span className="text-violet-300 font-bold">Zendesk</span>.
          </p>

          <div className="mt-7 flex items-center justify-center gap-3 flex-wrap">
            <a
              href="/register"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-extrabold px-6 py-3.5 rounded-full text-sm uppercase tracking-wider hover:from-violet-400 hover:to-fuchsia-400 transition-all shadow-[0_8px_30px_rgba(168,85,247,0.4)]"
              data-testid="hero-cta-start-free"
            >
              {dict.cta_primary} <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="#scan-results-anchor"
              className="inline-flex items-center gap-2 bg-white/5 border border-white/10 text-white font-bold px-6 py-3.5 rounded-full text-sm uppercase tracking-wider hover:bg-white/10 transition-colors"
              data-testid="hero-cta-demo"
            >
              {dict.cta_secondary}
            </a>
          </div>
        </div>

        {/* MOCKUP DASHBOARD */}
        <div className="relative max-w-5xl mx-auto">
          <div className="absolute -inset-4 lg:-inset-8 bg-gradient-to-br from-violet-500/20 via-fuchsia-500/10 to-cyan-500/15 blur-3xl rounded-3xl pointer-events-none" />

          <div className="relative bg-[#0B0617]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl overflow-hidden" data-testid="agent-os-mockup">
            {/* Header bar */}
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/5 bg-black/40">
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  <span className="w-2.5 h-2.5 rounded-full bg-rose-500/70" />
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
                </div>
                <span className="text-[10px] tracking-[0.3em] uppercase text-violet-300 font-bold ml-3">TopRankPulse Agent OS · v1.0</span>
              </div>
              <div className="flex items-center gap-2 text-[10px] text-emerald-300 font-bold">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                {dict.all_operational}
              </div>
            </div>

            <div className="grid grid-cols-12 gap-0">
              {/* Sidebar */}
              <aside className="col-span-12 md:col-span-3 border-r border-white/5 bg-black/30 py-4 px-2">
                <div className="text-[9px] tracking-[0.25em] uppercase text-gray-500 font-bold px-2 mb-2">{dict.sidebar_agents}</div>
                {SIDEBAR_ITEMS.map((it, i) => (
                  <SidebarRow key={i} {...it} t={dict} active={i === 0} />
                ))}
              </aside>

              {/* Main grid */}
              <div className="col-span-12 md:col-span-9 p-3 lg:p-5 grid grid-cols-12 gap-3">
                {/* Big KPI card */}
                <div className="col-span-12 lg:col-span-7 rounded-xl bg-gradient-to-br from-violet-900/40 via-fuchsia-900/30 to-violet-900/40 border border-violet-400/20 p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[10px] tracking-[0.25em] uppercase text-emerald-300 font-bold mb-1">{dict.live_24h}</div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-4xl lg:text-5xl font-black bg-gradient-to-r from-violet-200 via-fuchsia-200 to-pink-200 bg-clip-text text-transparent">{emails}</span>
                        <span className="text-xs text-gray-400 font-semibold">{dict.actions_total}</span>
                      </div>
                    </div>
                    <div className="hidden sm:flex flex-col items-end gap-1">
                      <Badge color="emerald">{avgSec}s {dict.avg}</Badge>
                      <Badge color="violet">{noEdit}% {dict.no_edit}</Badge>
                    </div>
                  </div>
                  {/* Mini chart */}
                  <div className="mt-4 flex items-end gap-1 h-12">
                    {[18, 25, 21, 34, 30, 42, 38, 51, 47, 58, 64, 71, 68, 78, 84, 92].map((h, i) => (
                      <div key={i} className="flex-1 rounded-t bg-gradient-to-t from-violet-500/80 to-fuchsia-400/80" style={{ height: `${h}%` }} />
                    ))}
                  </div>
                </div>

                {/* Connected stores card */}
                <div className="col-span-12 lg:col-span-5 rounded-xl bg-white/[0.03] border border-white/10 p-4">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-fuchsia-300 font-bold mb-1">{dict.connected}</div>
                  <div className="text-2xl font-black mt-1">{reachStores.toLocaleString(language || "sk")}</div>
                  <div className="text-[10px] text-gray-400 mt-0.5">{dict.shops_active}</div>
                  <div className="mt-3 flex gap-1.5">
                    {["Shopify", "WP", "Webflow", "Wix"].map(s => (
                      <span key={s} className="text-[9px] bg-white/5 border border-white/10 rounded px-1.5 py-0.5 text-gray-300 font-semibold">{s}</span>
                    ))}
                  </div>
                </div>

                {/* Blog Agent card */}
                <MiniPanel icon={FileText} title={dict.blog_agent} value="+12" sub={dict.blog_sub} colorFrom="from-emerald-500/30" testid="mock-blog" />

                {/* Returns Triage card */}
                <MiniPanel icon={Mail} title={dict.returns_agent} value="0:17" sub={dict.returns_sub} colorFrom="from-rose-500/30" testid="mock-returns" />

                {/* Catalog Brain card */}
                <MiniPanel icon={Sparkles} title={dict.catalog_agent} value="38" sub={dict.catalog_sub} colorFrom="from-violet-500/30" testid="mock-catalog" />

                {/* Inventory + Phase 3 row */}
                <div className="col-span-12 rounded-xl bg-white/[0.03] border border-white/10 p-3 flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <Package className="w-4 h-4 text-amber-400" />
                    <div>
                      <div className="text-[11px] font-bold text-white">{dict.inventory_agent}</div>
                      <div className="text-[10px] text-gray-400">{dict.inventory_sub}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-[10px]">
                    <ChipDot color="emerald">{dict.shipping}</ChipDot>
                    <ChipDot color="violet">{dict.accounting}</ChipDot>
                    <ChipDot color="amber">{dict.tax_vat}</ChipDot>
                    <ChipDot color="rose">{dict.chargeback}</ChipDot>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function normalizeLang(l) {
  const x = (l || "sk").toLowerCase().split("-")[0];
  return ["sk", "en", "cs", "de"].includes(x) ? x : "en";
}

function TrustChip({ icon: Icon, text }) {
  return (
    <div className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-full px-3 py-1.5">
      <Icon className="w-3.5 h-3.5 text-emerald-400" />
      <span className="font-semibold text-gray-200">{text}</span>
    </div>
  );
}

function Badge({ color, children }) {
  const map = {
    emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
    violet: "bg-violet-500/15 text-violet-200 border-violet-400/30",
  };
  return <span className={`text-[10px] font-bold border rounded-full px-2 py-0.5 ${map[color]}`}>{children}</span>;
}

function ChipDot({ color, children }) {
  const map = {
    emerald: "bg-emerald-400",
    violet: "bg-violet-400",
    amber: "bg-amber-400",
    rose: "bg-rose-400",
  };
  return (
    <span className="inline-flex items-center gap-1 text-gray-300">
      <span className={`w-1.5 h-1.5 rounded-full ${map[color]}`} /> {children}
    </span>
  );
}

function SidebarRow({ icon: Icon, label, active, badge, t }) {
  return (
    <div className={`flex items-center gap-2 px-2 py-1.5 rounded-lg ${active ? "bg-white/10" : "hover:bg-white/5"}`}>
      <Icon className={`w-3.5 h-3.5 ${active ? "text-violet-300" : "text-gray-500"}`} />
      <span className={`text-[11px] font-semibold ${active ? "text-white" : "text-gray-400"}`}>{t[label] || label}</span>
      {badge && <span className="ml-auto text-[8px] bg-emerald-500/20 text-emerald-300 px-1.5 py-0.5 rounded">{badge}</span>}
    </div>
  );
}

function MiniPanel({ icon: Icon, title, value, sub, colorFrom, testid }) {
  return (
    <div className={`col-span-12 sm:col-span-6 lg:col-span-4 rounded-xl border border-white/10 bg-gradient-to-br ${colorFrom} to-transparent p-3.5`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <Icon className="w-4 h-4 text-white/80" />
        <span className="text-[9px] tracking-[0.2em] uppercase text-emerald-300/80 font-bold">LIVE</span>
      </div>
      <div className="text-[11px] uppercase tracking-wider text-gray-300 font-bold mt-2">{title}</div>
      <div className="text-2xl font-black text-white mt-0.5">{value}</div>
      <div className="text-[10px] text-gray-400 mt-0.5">{sub}</div>
    </div>
  );
}

const SIDEBAR_ITEMS = [
  { icon: FileText, label: "blog_agent" },
  { icon: Mail, label: "returns_agent", badge: "LIVE" },
  { icon: Sparkles, label: "catalog_agent" },
  { icon: Package, label: "inventory_agent" },
  { icon: Receipt, label: "accounting" },
  { icon: Truck, label: "shipping" },
  { icon: Megaphone, label: "ad_studio" },
];

const COPY = {
  sk: {
    kicker: "Agent OS · vždy zapnutý",
    h1_pre: "Prestaň platiť 7 SaaS-ov.",
    h1_accent: "Jeden Agent OS",
    h1_post: " urobí všetko, kým spíš.",
    subhead: "Blog, returns, inventory, catalog SEO, faktúry, chargebacks — autonómne AI agenti, ktorí robia robotu za teba každú noc. Bez nastavovania, bez kreditky.",
    trust_card: "Bez kreditky",
    trust_setup: "Bez nastavovania",
    trust_eu: "EU-native · GDPR",
    trust_speed: "Hotové za 60 sekúnd",
    switched_from: "e-shopov prešlo zo",
    and: "a",
    cta_primary: "Vyskúšať zadarmo",
    cta_secondary: "Pozrieť demo scan",
    all_operational: "VŠETKO BEŽÍ",
    sidebar_agents: "Agenti",
    live_24h: "Akcie za 24h",
    actions_total: "AI úkonov",
    connected: "Pripojené e-shopy",
    shops_active: "aktívnych obchodov v EÚ",
    avg: "priemer",
    no_edit: "bez úprav",
    blog_agent: "Blog Agent",
    blog_sub: "článkov tento týždeň",
    returns_agent: "Returns Triage",
    returns_sub: "priemer / reply",
    catalog_agent: "Catalog Brain",
    catalog_sub: "SEO fixov pripravených",
    inventory_agent: "Inventory · Phase 3",
    inventory_sub: "5 agentov pracuje paralelne",
    accounting: "Účtovníctvo",
    shipping: "Doprava",
    tax_vat: "DPH",
    chargeback: "Chargeback",
  },
  en: {
    kicker: "Agent OS · always on",
    h1_pre: "Stop paying for 7 SaaS tools.",
    h1_accent: "One Agent OS",
    h1_post: " runs your store while you sleep.",
    subhead: "Blog, returns, inventory, catalog SEO, invoices, chargebacks — autonomous AI agents doing the work for you every night. No setup, no credit card.",
    trust_card: "No credit card",
    trust_setup: "No setup",
    trust_eu: "EU-native · GDPR",
    trust_speed: "Ready in 60 seconds",
    switched_from: "stores switched from",
    and: "and",
    cta_primary: "Start free",
    cta_secondary: "See demo scan",
    all_operational: "ALL SYSTEMS GO",
    sidebar_agents: "Agents",
    live_24h: "Actions last 24h",
    actions_total: "AI actions",
    connected: "Connected stores",
    shops_active: "active EU shops",
    avg: "avg",
    no_edit: "no manual edits",
    blog_agent: "Blog Agent",
    blog_sub: "articles this week",
    returns_agent: "Returns Triage",
    returns_sub: "avg / reply",
    catalog_agent: "Catalog Brain",
    catalog_sub: "SEO fixes ready",
    inventory_agent: "Inventory · Phase 3",
    inventory_sub: "5 agents running in parallel",
    accounting: "Accounting",
    shipping: "Shipping",
    tax_vat: "Tax/VAT",
    chargeback: "Chargeback",
  },
  cs: {
    kicker: "Agent OS · vždy zapnutý",
    h1_pre: "Přestaň platit 7 SaaS-ů.",
    h1_accent: "Jeden Agent OS",
    h1_post: " udělá všechno, dokud spíš.",
    subhead: "Blog, returns, inventory, catalog SEO, faktury, chargebacks — autonomní AI agenti, kteří dělají práci za tebe každou noc. Bez nastavování, bez karty.",
    trust_card: "Bez karty",
    trust_setup: "Bez nastavování",
    trust_eu: "EU-native · GDPR",
    trust_speed: "Hotové za 60 sekund",
    switched_from: "e-shopů přešlo ze",
    and: "a",
    cta_primary: "Vyzkoušet zdarma",
    cta_secondary: "Podívat se na demo",
    all_operational: "VŠE BĚŽÍ",
    sidebar_agents: "Agenti",
    live_24h: "Akce za 24h",
    actions_total: "AI úkonů",
    connected: "Připojené e-shopy",
    shops_active: "aktivních obchodů v EU",
    avg: "průměr",
    no_edit: "bez úprav",
    blog_agent: "Blog Agent",
    blog_sub: "článků tento týden",
    returns_agent: "Returns Triage",
    returns_sub: "průměr / reply",
    catalog_agent: "Catalog Brain",
    catalog_sub: "SEO fixů připravených",
    inventory_agent: "Inventory · Phase 3",
    inventory_sub: "5 agentů běží paralelně",
    accounting: "Účetnictví",
    shipping: "Doprava",
    tax_vat: "DPH",
    chargeback: "Chargeback",
  },
  de: {
    kicker: "Agent OS · immer an",
    h1_pre: "Hör auf, 7 SaaS-Tools zu bezahlen.",
    h1_accent: "Ein Agent-OS",
    h1_post: " kümmert sich um deinen Shop, während du schläfst.",
    subhead: "Blog, Retouren, Lager, Katalog-SEO, Rechnungen, Chargebacks — autonome KI-Agenten, die jede Nacht die Arbeit für dich erledigen. Keine Einrichtung, keine Kreditkarte.",
    trust_card: "Keine Kreditkarte",
    trust_setup: "Keine Einrichtung",
    trust_eu: "EU-native · DSGVO",
    trust_speed: "In 60 Sekunden bereit",
    switched_from: "Shops gewechselt von",
    and: "und",
    cta_primary: "Kostenlos starten",
    cta_secondary: "Demo ansehen",
    all_operational: "ALLES LÄUFT",
    sidebar_agents: "Agenten",
    live_24h: "Aktionen letzte 24h",
    actions_total: "KI-Aktionen",
    connected: "Verbundene Shops",
    shops_active: "aktive EU-Shops",
    avg: "Schnitt",
    no_edit: "ohne Bearbeitung",
    blog_agent: "Blog Agent",
    blog_sub: "Artikel diese Woche",
    returns_agent: "Returns Triage",
    returns_sub: "Schnitt / Antwort",
    catalog_agent: "Catalog Brain",
    catalog_sub: "SEO-Fixes bereit",
    inventory_agent: "Inventar · Phase 3",
    inventory_sub: "5 Agenten parallel aktiv",
    accounting: "Buchhaltung",
    shipping: "Versand",
    tax_vat: "MwSt.",
    chargeback: "Chargeback",
  },
};
